import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';

import { useCart } from '~Components/CartProvider';
import { useBongLayout } from '~Providers/BongLayout';
import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import LoadingDataScreen from '~ComponentsImproved/LoadingDataScreen';

import ItemButton from './ItemButton';
import Categories from './Categories';
import StagedBundleGrid from './StagedBundleGrid';

import ItemModifications from './ItemModifications';
import { useI18n, useSelector } from '~Hooks';
import { CartItem, LocalItem, LocalVariation } from '~Types';
import Cart from './Cart';
import { v4 as uuid } from 'uuid';
import { useDeviceInfo, useLogger } from '~Hooks';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import { useEntryFinder } from '~Components/EntryFinder';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { extractServiceChargePercentage, isServiceChargeItem } from '~Utils/serviceCharge';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { usePage } from '~Scenes/Handy/Context/PageProvider';
import QSRCheque from '../HandyQSR/QSRCheque';
import { useStorage } from '~Components/Storage';
import { SERVICE_TYPE } from '../../../../../__queries__/globalTypes';
import { SelectedUpsales } from '~Scenes/POS/Components/ItemModifications';
import { useApolloClient } from '@apollo/client';
import { ITEM_FRAGMENT } from '~Scenes/POS/Queries/query';
import ToggleServiceType from './ToggleServiceType';
import SearchItem from './SearchItem';
import FailedPrintsAlert from '~Scenes/Handy/Components/FailedPrintsAlert';

const Wrapper = styled.div<{
  isPhone?: boolean;
  isQSR?: boolean;
}>`
  position: fixed;
  position: ${({ isQSR }) => (isQSR ? 'relative' : 'fixed')};
  height: ${({ isQSR }) => (isQSR ? '100%' : 'auto')};
  top: 0;
  bottom: 0;
  display: flex;
  background: var(--colorBlueGray1100);
  overflow: hidden;
  width: 100vw;
  z-index: ${({ isQSR }) => (isQSR ? '1' : '5')};
  padding-top: ${({ isPhone, isQSR }) => (isPhone && !isQSR ? '42px' : '0px')};
`;

const ItemOuterWrapper = styled.div`
  flex: 1;
  overflow: auto;
  padding-bottom: 80px;
`;

export const ItemWrapper = styled.div<{ hasActiveLayout: boolean }>`
  margin: 5px;
  line-height: 0px;
  display: grid;
  align-self: flex-start;
  grid-auto-rows: 60px;
  grid-gap: 6px;

  ${({ hasActiveLayout }) => !hasActiveLayout && 'grid-template-columns: repeat(3, 1fr)'}
`;

interface Props {
  onSubmit: (items?: CartItem[]) => void;
  onCancel: () => void;
}

const Ordering: React.FC<Props> = ({ onSubmit, onCancel }) => {
  const venueId = useSelector((state) => state.venueId);

  const layout = useBongLayout();
  const loading = layout.loading;

  const deviceInfo = useDeviceInfo();
  const logger = useLogger('handy');
  const tableAPI = useTableApi();
  const { authedStaff } = useStaffContext();
  const { selectedTable } = useTableContext();
  const { selectedCheque } = useChequeContext();
  const findEntryFromItem = useEntryFinder();
  const universe = useTableUniverse();
  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);
  const cheques = universe.cheques.filter((c) => c.tableId === selectedTable?.id);
  const competitions = useCompetition();
  const hasActiveLayout = layout.buttons.some((button) => button.x !== null && button.y !== null);
  const page = usePage();
  const isQSR = page?.current === 'POS';
  const [storage] = useStorage();

  const [autostartPayment, setAutostartPayment] = useState(false);

  const client = useApolloClient();
  const cart = useCart();
  const { i18n } = useI18n();

  /**
   * The item that currently has the Modification selector open.
   * Setting this state opens the Modification selector.
   * This applies if the item has at least one assigned Modification Group or Upsell Group.
   */
  const [currentItemWithModifications, setCurrentItemWithModifications] = useState<
    (Item & { volume?: number; message?: string }) | null
  >(null);

  /**
   * State for subpage navigation (e.g., staged bundles)
   */
  const [subPage, setSubPage] = useState<any>(null);

  if (cart.setItems === undefined) {
    console.warn('Ordering used outside CartProvider.');
  }

  type AddItemType = {
    item: Item;
    variations?: LocalVariation[];
    removedVariations?: LocalVariation[];
    upsaleItems?: SelectedUpsales;
    manualPrice?: number;
    volume?: number;
    message?: string;
  };

  const handleAddItem = useCallback(
    ({ item, variations, removedVariations, manualPrice, volume, message }: AddItemType) => {
      const idInCart = uuid();

      const category = layout.itemToCategoryMap[item.id];
      if (!category) {
        logger.warn('handleAddItem: Could not find category for item', {
          itemId: item.id,
          itemTitle: item.titleLang,
          categoryMap: {
            data: layout.itemToCategoryMap,
          },
        });
      }

      const takeAwayVatRate = category?.ukTakeAwayVatRate;

      const itemObj: LocalItem = {
        id: idInCart,
        itemId: item.id,
        categoryId: category?.id ?? '',
        description: '',
        removedVariations: removedVariations ?? [],
        title: i18n(item.titleLang),
        variations: variations ?? [],
        vatRate: item.vatRate,
        takeAwayVatRate: takeAwayVatRate,
        courseType: item.courseType,
        message: message ?? '',
        manualName: item.manualName,
        volumeSuffix: item.volumeSuffix,
        disableMembershipDiscount: item.disableMembershipDiscount ?? false,
      };

      if (manualPrice) {
        itemObj.price = manualPrice;
      }

      // Get the entry so we can get the id and the price (if volume)
      const entry = findEntryFromItem(item.id);

      if (volume) {
        const titleWithVolume = `${volume}${item.volumeSuffix} ${i18n(item.titleLang)}`;

        itemObj.title = titleWithVolume;
        itemObj.price = entry.price * volume;
      }

      cart.addItem({ ...itemObj, entryId: entry.id });
    },
    [layout.itemToCategoryMap, logger, findEntryFromItem, i18n, cart.addItem],
  );

  const handleItemPress = useCallback(
    (item: Item, manualPrice?: number, volume?: number, message?: string) => {
      const hasModificationGroups = item.modificationGroups.length > 0;

      const hasUpsellGroups = item.upsales.filter((u) => !u.hideInPOS)?.length > 0;

      // If we have modifications, show the modification selection. If we have manualName or manualPrice, we skip the selection
      if ((hasModificationGroups || hasUpsellGroups) && !item.manualName && !manualPrice) {
        setCurrentItemWithModifications({ ...item, volume, message });
      } else {
        // If we don't have modifications, we call handleAdd directly
        handleAddItem({
          item,
          manualPrice,
          volume,
          message,
        });
      }
    },
    [handleAddItem, setCurrentItemWithModifications],
  );

  const onPageClicked = useCallback((pageClicked: { id: string; name: string }) => {
    layout.browseToSubpage(pageClicked);
  }, []);

  const handleSendOrder = async () => {
    if (isQSR) {
      // We handle the order submit in the QSR scene instead
      if (storage.handyDirectPayment) {
        setAutostartPayment(true);
      }
      onSubmit(cart.packagedItems);
      return;
    }
    // When ordering from tables view, we do not have a selected cheque
    // In those cases, use the 0th cheque instead.
    const submitToCheque = selectedCheque ?? cheques[0];

    if (!submitToCheque) {
      logger.error('handleOrderSubmit: Could not determine cheque', {
        selectedCheque,
        selectedTable,
        cheques,
      });
      return;
    }

    logger.info('Submitted order', {
      items: cart.packagedItems,
      cheque: submitToCheque,
      selectedTable,
      authedStaff,
    });

    cart.packagedItems.forEach((item) => {
      if (isServiceChargeItem(item.title)) {
        const percentage = extractServiceChargePercentage(item.title);
        tableAPI.addServiceCharge({
          categoryId: item.categoryId,
          chequeId: submitToCheque.id,
          entryId: item.entryId,
          itemId: item.itemId,
          percentage,
          vatRate: item.vatRate,
        });
      }
    });

    /** Items that is filtered from Service Charge & Messages */
    const justRegularItems = cart.packagedItems.filter(
      (item) => !isServiceChargeItem(item.title) && !item.title.includes('[m]'),
    );

    const itemsToAdd = justRegularItems.map((item) => ({
      ...item,
      amount: item.originalAmount || item.amount, // The TSR handles the offers calculation so we need to send the original amount for it to calculate the discount correctly
      discount: 0, // The TSR handles the offers calculation so we need to send 0 discount for it to calculate the discount correctly
      entryId: findEntryFromItem(item.itemId).id,
    }));

    tableAPI.addItems(itemsToAdd, submitToCheque.id);

    if (authedStaff?.id) {
      competitions.addSale({
        staffId: authedStaff.id,
        items: cart.packagedItems,
      });
    }

    const messages = cart.items
      .filter((item) => item.title.includes('[m]'))
      .map((item) => {
        const title = item.title.substring(3).trim();

        return {
          ...item,
          title,
        };
      });

    tableAPI.log.addItems(submitToCheque.id, selectedTable?.id ?? '', cart.packagedItems);

    messages.forEach((message) => {
      tableAPI.addMessage(message.title, submitToCheque.id, message.courseType?.label);
    });

    logger.info('[DEBUG MODE] Sending kitchen bong', {
      data: {
        cheque: submitToCheque,
      },
    });

    const items = cart.packagedItems.filter(
      (pi) => !isServiceChargeItem(pi.title) && !pi.title.includes('[m]'),
    );

    tableAPI.sendKitchenBong(
      messages,
      items,
      selectedTable?.code,
      selectedTable?.code,
      selectedTable?.id,
      submitToCheque!.coverage!,
      SERVICE_TYPE.EAT_HERE,
      null,
      cart.ticketMessages,
    );

    cart.ticketMessages.map((tm) => {
      tableAPI.addMessage(tm.message, submitToCheque.id);
    });

    // Reduce inventory for each item (if the item has an inventory count)
    items.forEach((item) => {
      const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

      if (invItem && typeof invItem.count === 'number') {
        inventoryApi.reduceStock(invItem.itemId, item.fraction, 'Order');
      }
    });

    onSubmit();
  };

  const itemsOnCheque = useMemo(
    () => [...universe.items].filter((item) => item.chequeId === selectedCheque?.id),
    [universe.items],
  );

  // If we are in QSR mode, we only want to count the items in the cart otherwise it will be doubled
  const itemsForCount = isQSR ? cart.packagedItems : [...itemsOnCheque, ...cart.packagedItems];

  // Check if subPage is a staged bundle
  const isStagedBundle = subPage && (subPage as any).__stagedBundle;

  // Build category items list and all items array for staged bundle
  const categoryItems = useMemo(() => {
    return layout.buttons
      .filter((button) => button.nodeId && layout.itemIdMap[button.nodeId])
      .map((button) => {
        const item = layout.itemIdMap[button.nodeId];
        const category = layout.itemToCategoryMap[button.nodeId];
        return {
          id: category?.id ?? '',
          itemId: button.nodeId,
          title: button.label,
          color: button.color,
          descriptionLang: item?.descriptionLang,
          manualPrice: item?.manualPrice ?? false,
          volumeSuffix: item?.volumeSuffix,
        };
      });
  }, [layout.buttons, layout.itemIdMap, layout.itemToCategoryMap]);

  // Convert itemIdMap to array for staged bundle
  const allItems = useMemo(() => {
    return Object.values(layout.itemIdMap);
  }, [layout.itemIdMap]);

  return (
    <>
      {currentItemWithModifications && (
        <ItemModifications
          item={currentItemWithModifications}
          onCancel={() => setCurrentItemWithModifications(null)}
          onComplete={(
            variations: LocalVariation[],
            removedVariations: LocalVariation[],
            upsaleItems: SelectedUpsales,
          ) => {
            handleAddItem({
              item: currentItemWithModifications,
              variations,
              removedVariations,
              volume: currentItemWithModifications.volume,
              message: currentItemWithModifications.message,
            });

            if (upsaleItems.length > 0) {
              upsaleItems.forEach((item) => {
                const fullUpsaleItemData = client.readFragment<Item>({
                  id: `Item${item.id}`,
                  fragment: ITEM_FRAGMENT,
                });

                if (fullUpsaleItemData) {
                  handleAddItem({
                    item: fullUpsaleItemData,
                    variations: [],
                    removedVariations: [],
                  });
                }
              });
            }

            setCurrentItemWithModifications(null);
          }}
        />
      )}
      <Wrapper isPhone={deviceInfo?.isPhone} isQSR={isQSR}>
        {loading?.isLoading ? (
          <LoadingDataScreen
            onClose={onCancel}
            progress={loading.progress}
            progressText={loading.progressText}
          />
        ) : (
          <>
            {<SearchItem handleItemPress={handleItemPress} setSubPage={setSubPage} />}
            <Categories clearSubpage={() => setSubPage(null)} />
            <ItemOuterWrapper>
              {isStagedBundle ? (
                <StagedBundleGrid
                  offerName={(subPage as any).offerName}
                  stages={(subPage as any).stages}
                  categoryItems={categoryItems}
                  allItems={allItems}
                  venueId={venueId ?? ''}
                  itemToCategoryMap={layout.itemToCategoryMap}
                  onAddItem={handleItemPress}
                  onComplete={() => {
                    logger.info('Staged bundle completed');
                    setSubPage(null); // Go back to main grid
                  }}
                />
              ) : (
                <ItemWrapper hasActiveLayout={hasActiveLayout}>
                  {layout.buttons.map((button) => (
                    <ItemButton
                      key={button.nodeId}
                      button={button}
                      onPress={handleItemPress}
                      onPageClicked={onPageClicked}
                      count={
                        itemsForCount.filter((item) => item.itemId === button.nodeId).length ?? 0
                      }
                      setSubPage={setSubPage}
                    />
                  ))}
                </ItemWrapper>
              )}
            </ItemOuterWrapper>
            <Cart
              onSubmit={handleSendOrder}
              onCancel={onCancel}
              handleItemPress={handleItemPress}
              setSubPage={setSubPage}
            />
          </>
        )}
      </Wrapper>

      {isQSR && (
        <QSRCheque
          serviceType={cart.serviceType}
          clearCart={cart.clear}
          setAppliedDiscountForCart={cart.setAppliedDiscount}
          initiatePaymentOnOpen={autostartPayment}
          onAutoPaymentInitiated={() => setAutostartPayment(false)}
        />
      )}

      {isQSR && !storage.hideServiceType && (
        <ToggleServiceType
          currentServiceType={cart.serviceType}
          updateServiceType={cart.updateServiceType}
          isPhone={deviceInfo?.isPhone}
        />
      )}

      {cart.items.length === 0 && <FailedPrintsAlert />}
    </>
  );
};

export default Ordering;
